import type { Moment } from 'moment';

/**
 * Calculates age in years based on birth date and target date
 * Returns the age someone will be turning on their next birthday occurrence.
 *
 * @param birthDate - Birth date as ISO string (YYYY-MM-DD format)
 * @param targetDate - Target date to calculate age against (defaults to current date)
 * @returns Age in years, or null if birth date is invalid
 *
 * @example
 * // Person born April 14, 1961, checked on October 30, 2025
 * calculateAge('1961-04-14', new Date('2025-10-30')) // Returns 65 (turning 65 on April 14, 2026)
 *
 * @note Leap year birthdays (Feb 29):
 * In non-leap years, Feb 29 is coerced to March 1 by JavaScript's Date constructor.
 * This follows the common convention that people born on Feb 29 celebrate on March 1 in non-leap years.
 * Example: Born Feb 29, 2000, checked on March 1, 2025 → Returns 25 (celebrating 25th birthday today)
 */
export const calculateAge = (birthDate: string, targetDate?: Date): number | null => {
	if (!birthDate) {
		return null;
	}

	try {
		// Parse date string manually to avoid timezone issues
		// Assumes birthDate is in YYYY-MM-DD format
		const [year, month, day] = birthDate.split('-').map(num => parseInt(num, 10));
		const birth = new Date(year, month - 1, day); // month is 0-indexed
		const target = targetDate || new Date();

		// Validate the birth date
		if (isNaN(birth.getTime())) {
			return null;
		}

		// Don't calculate age for future birth dates
		if (birth > target) {
			return null;
		}

		// Calculate age they'll be on their next birthday
		// For "Turning X" dashboards, we want the age they will turn on their next birthday
		let age = target.getFullYear() - birth.getFullYear();

		// Check if birthday has already passed this year.
		// Normalize target to midnight so a same-day check (target has a time component,
		// birthdayThisYear is midnight) doesn't count the birthday as already passed.
		const birthdayThisYear = new Date(target.getFullYear(), birth.getMonth(), birth.getDate());
		const targetMidnight = new Date(target.getFullYear(), target.getMonth(), target.getDate());
		if (targetMidnight > birthdayThisYear) {
			// Birthday already happened this year, so next birthday is next year
			age += 1;
		}

		return age >= 0 && age <= 150 ? age : null;
	} catch {
		return null;
	}
};

/**
 * Checks if a given date string represents a valid date
 * @param dateString - Date string to validate
 * @returns True if valid date, false otherwise
 */
export const isValidDateString = (dateString: string): boolean => {
	if (!dateString) {
		return false;
	}

	try {
		const date = new Date(dateString);
		return !isNaN(date.getTime());
	} catch {
		return false;
	}
};

/** "Nov 3rd, 2026 - Nov 5th, 2026 · All day", or a single date when the event is one day. */
export const formatAllDayDateRange = (firstDay: Moment, lastDay?: Moment) => {
	const first = firstDay.format('MMM Do, YYYY');
	const last = lastDay?.format('MMM Do, YYYY');
	return `${last && last !== first ? `${first} - ${last}` : first} · All day`;
};
